import React, { useState, useMemo } from 'react';
import { useAuthentication } from '@hooks/authentication';
import { Button, DatePicker, Table, Card, Row, Col, Spin, Popover } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as api from '@apis/statistics';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Chart.js 등록
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

// 항목별 설명 정의
const itemDescriptions: { [key: string]: string } = {
  totalPaymentAmount: '고객이 결제한 전체 금액',
  totalPointAmount: '포인트로 결제한 금액',
  totalCouponDiscountAmount: '쿠폰 할인 금액',
  totalMembershipDiscountAmount: '멤버십 할인 금액',
  totalBillingAmount: '총 결제 금액 - 포인트 - 쿠폰 - 멤버십 할인',
  totalRefundAmount: '전체 환불 금액 (괄호 안: 총 결제 금액 대비 환불 비율)',
  totalMaxCalculatedRefundAmount: '과거 최고 환불율로 예상한 최대 환불 금액',
  netTransactionAmountMaxRefundEstimated: '실결제 금액 - 최대 환불 예상 금액',
  netTransactionAmount: '실결제 금액 - 환불 금액',
  totalEstimatedSettlementAmount: '파트너에게 지급될 예상금액 (파트너별 수수료율 반영, 챌린저스는 35%로 API에서 계산)',
  totalTransferAmount: '정산되어 파트너에게 지급 완료된 금액',
  profitMaxRefundEstimated: '실결제 금액 - 최대 환불 예상 금액 - 총 정산금액',
  profitMaxRefundAndSettlementEstimated: '실결제 금액 - 최대 환불 예상 금액 - 총 정산예상금액',
  profitNotSettled: '실결제 금액 - 환불 금액 - 총 정산예상금액',
  profit: '실결제 금액 - 환불 금액 - 총 정산금액',
  pgFee: '(실결제 금액 - 실환불 금액) × 수수료율 (앱: 3.3%, 네이버스토어: 5.3%, 카카오 예약하기: 5.4%, 이지웰: 0%)',
  pgFeeMaxRefundEstimated: '(실결제 금액 - 최대 환불 예상 금액) × 수수료율',
  netProfitMaxRefundEstimated: '실결제 금액 - 최대 환불 예상 금액 - 총 정산금액 - PG 및 채널 수수료',
  netProfitMaxRefundAndSettlementEstimated: '실결제 금액 - 최대 환불 예상 금액 - 총 정산예상금액 - PG 및 채널 수수료',
  netProfitEstimated: '실결제 금액 - 환불 금액 - 총 정산예상금액 - PG 및 채널 수수료',
  netProfit: '실결제 금액 - 환불 금액 - 총 정산금액 - PG 및 채널 수수료',
};

// 순거래액 행 스타일
const netTransactionRowStyle = `
  .net-transaction-row {
    border-bottom: 3px solid #1890ff !important;
  }
  .net-transaction-row td {
    border-bottom: 3px solid #1890ff !important;
  }
`;

// 스타일을 DOM에 추가
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = netTransactionRowStyle;
  document.head.appendChild(styleElement);
}

interface NetSalesReportData {
  channel: string;
  totalPaymentAmount: number;
  totalBillingAmount: number;
  totalPointAmount: number;
  totalCouponDiscountAmount: number;
  totalMembershipDiscountAmount: number;
  totalEstimatedSettlementAmount: number;
  totalTransferAmount: number;
  totalRefundAmount: number;
  totalRefundBillingAmount: number;
  estimatedTotalRefundBillingAmount: number;
  pgFee: number;
  estimatedPgFee: number;
}

interface DailyNetSalesData {
  date: string;
  channel: string;
  totalBillingAmount: number;
  totalRefundBillingAmount: number;
  totalEstimatedSettlementAmount: number;
  pgFee: number;
  netProfit: number;
}

const NetSalesReportPage: React.FC = () => {
  const [searchDate, setSearchDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: moment().subtract(30, 'days').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NetSalesReportData[]>([]);
  const [dailyData, setDailyData] = useState<DailyNetSalesData[]>([]);

  useAuthentication();

  const handleSearch = async () => {
    setLoading(true);
    try {
      const [netSalesResult, dailyResult] = await Promise.all([
        api.getNetSalesReport(
          moment(searchDate.startDate).format('YYYY-MM-DD'),
          moment(searchDate.endDate).format('YYYY-MM-DD'),
        ),
        api.getDailyNetSalesByChannel(
          moment(searchDate.startDate).format('YYYY-MM-DD'),
          moment(searchDate.endDate).format('YYYY-MM-DD'),
        ),
      ]);

      setData(netSalesResult.data);
      setDailyData(dailyResult.data);
    } catch (error) {
      toast.error('데이터 조회 중 오류가 발생했습니다.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (data.length === 0) {
      toast.warning('내보낼 데이터가 없습니다.');
      return;
    }

    const transformedData = transformDataToRows(data);
    const worksheet = XLSX.utils.json_to_sheet(
      transformedData.map((item) => ({
        항목: item.item,
        앱: item.app.toLocaleString(),
        네이버스토어: item['naver-store'].toLocaleString(),
        '카카오 예약': item['kakao-reservation'].toLocaleString(),
        이지웰: (item as any).ezwel?.toLocaleString?.() ?? item['ezwel']?.toLocaleString?.(),
        합계: item.total.toLocaleString(),
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '이익관리');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const fileName = `순매출리포트_${moment(searchDate.startDate).format('YYYY-MM-DD')}_${moment(
      searchDate.endDate,
    ).format('YYYY-MM-DD')}.xlsx`;
    saveAs(blob, fileName);

    toast.success('엑셀 파일이 다운로드되었습니다.');
  };

  const columns: ColumnsType<any> = [
    {
      title: '항목',
      dataIndex: 'item',
      key: 'item',
      width: 200,
      fixed: 'left',
      render: (item: any, record: any) => {
        const description = itemDescriptions[record.key];
        if (!description) {
          return item;
        }

        // item이 React element인 경우와 문자열인 경우 모두 처리
        const itemContent = React.isValidElement(item) ? (
          React.cloneElement(item as React.ReactElement<any>, {
            style: {
              ...((item as React.ReactElement<any>).props?.style || {}),
              cursor: 'help',
              borderBottom: '1px dotted #999',
              display: 'inline-block',
            },
          })
        ) : (
          <span style={{ cursor: 'help', borderBottom: '1px dotted #999', display: 'inline-block' }}>{item}</span>
        );

        return (
          <Popover content={<div style={{ maxWidth: '300px' }}>{description}</div>} trigger="hover" placement="right">
            {itemContent}
          </Popover>
        );
      },
    },
    {
      title: '앱',
      dataIndex: 'app',
      key: 'app',
      width: 150,
      render: (value: number | { amount: number; percentage: string }, record: any) => {
        const isBoldRow =
          record.key === 'totalBillingAmount' ||
          record.key === 'profit' ||
          record.key === 'netProfit' ||
          record.key === 'netTransactionAmount';

        // 총 환불 금액 행인 경우 퍼센트와 함께 표시
        if (record.key === 'totalRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ fontWeight: isBoldRow ? 'bold' : 'normal' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#666' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대환불 예상 금액 행인 경우 옅은 회색으로 표시
        if (record.key === 'totalMaxCalculatedRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ color: '#999' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#999' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대환불 예상 반영 항목들인 경우 옅은 회색으로 표시
        if (
          [
            'netTransactionAmountMaxRefundEstimated',
            'profitMaxRefundEstimated',
            'profitMaxRefundAndSettlementEstimated',
            'pgFeeMaxRefundEstimated',
            'netProfitMaxRefundEstimated',
            'netProfitMaxRefundAndSettlementEstimated',
          ].includes(record.key)
        ) {
          const numValue = typeof value === 'object' ? value.amount : value;
          return <span style={{ color: '#999' }}>{numValue.toLocaleString()}원</span>;
        }

        // 일반 숫자 값인 경우
        const numValue = typeof value === 'object' ? value.amount : value;
        return isBoldRow ? (
          <span style={{ fontWeight: 'bold' }}>{numValue.toLocaleString()}원</span>
        ) : (
          <span>{numValue.toLocaleString()}원</span>
        );
      },
      align: 'right',
    },
    {
      title: '네이버스토어',
      dataIndex: 'naver-store',
      key: 'naver-store',
      width: 150,
      render: (value: number | { amount: number; percentage: string }, record: any) => {
        const isBoldRow =
          record.key === 'totalBillingAmount' ||
          record.key === 'profit' ||
          record.key === 'netProfit' ||
          record.key === 'netTransactionAmount';

        // 총 환불 금액 행인 경우 퍼센트와 함께 표시
        if (record.key === 'totalRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ fontWeight: isBoldRow ? 'bold' : 'normal' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#666' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대 환불 예상 금액 행인 경우 옅은 회색으로 표시
        if (record.key === 'totalMaxCalculatedRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ color: '#999' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#999' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대환불 예상 반영 항목들인 경우 옅은 회색으로 표시
        if (
          [
            'netTransactionAmountMaxRefundEstimated',
            'profitMaxRefundEstimated',
            'profitMaxRefundAndSettlementEstimated',
            'pgFeeMaxRefundEstimated',
            'netProfitMaxRefundEstimated',
            'netProfitMaxRefundAndSettlementEstimated',
          ].includes(record.key)
        ) {
          const numValue = typeof value === 'object' ? value.amount : value;
          return <span style={{ color: '#999' }}>{numValue.toLocaleString()}원</span>;
        }

        // 일반 숫자 값인 경우
        const numValue = typeof value === 'object' ? value.amount : value;
        return isBoldRow ? (
          <span style={{ fontWeight: 'bold' }}>{numValue.toLocaleString()}원</span>
        ) : (
          <span>{numValue.toLocaleString()}원</span>
        );
      },
      align: 'right',
    },
    {
      title: '카카오 예약',
      dataIndex: 'kakao-reservation',
      key: 'kakao-reservation',
      width: 150,
      render: (value: number | { amount: number; percentage: string }, record: any) => {
        const isBoldRow =
          record.key === 'totalBillingAmount' ||
          record.key === 'profit' ||
          record.key === 'netProfit' ||
          record.key === 'netTransactionAmount';

        // 총 환불 금액 행인 경우 퍼센트와 함께 표시
        if (record.key === 'totalRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ fontWeight: isBoldRow ? 'bold' : 'normal' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#666' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대 환불 예상 금액 행인 경우 옅은 회색으로 표시
        if (record.key === 'totalMaxCalculatedRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ color: '#999' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#999' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대환불 예상 반영 항목들인 경우 옅은 회색으로 표시
        if (
          [
            'netTransactionAmountMaxRefundEstimated',
            'profitMaxRefundEstimated',
            'profitMaxRefundAndSettlementEstimated',
            'pgFeeMaxRefundEstimated',
            'netProfitMaxRefundEstimated',
            'netProfitMaxRefundAndSettlementEstimated',
          ].includes(record.key)
        ) {
          const numValue = typeof value === 'object' ? value.amount : value;
          return <span style={{ color: '#999' }}>{numValue.toLocaleString()}원</span>;
        }

        // 일반 숫자 값인 경우
        const numValue = typeof value === 'object' ? value.amount : value;
        return isBoldRow ? (
          <span style={{ fontWeight: 'bold' }}>{numValue.toLocaleString()}원</span>
        ) : (
          <span>{numValue.toLocaleString()}원</span>
        );
      },
      align: 'right',
    },
    {
      title: '이지웰',
      dataIndex: 'ezwel',
      key: 'ezwel',
      width: 150,
      render: (value: number | { amount: number; percentage: string }, record: any) => {
        const isBoldRow =
          record.key === 'totalBillingAmount' ||
          record.key === 'profit' ||
          record.key === 'netProfit' ||
          record.key === 'netTransactionAmount';

        if (record.key === 'totalRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ fontWeight: isBoldRow ? 'bold' : 'normal' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#666' }}>({value.percentage}%)</div>
            </div>
          );
        }

        if (record.key === 'totalMaxCalculatedRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ color: '#999' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#999' }}>({value.percentage}%)</div>
            </div>
          );
        }

        if (
          [
            'netTransactionAmountMaxRefundEstimated',
            'profitMaxRefundEstimated',
            'profitMaxRefundAndSettlementEstimated',
            'pgFeeMaxRefundEstimated',
            'netProfitMaxRefundEstimated',
            'netProfitMaxRefundAndSettlementEstimated',
          ].includes(record.key)
        ) {
          const numValue = typeof value === 'object' ? value.amount : value;
          return <span style={{ color: '#999' }}>{numValue.toLocaleString()}원</span>;
        }

        const numValue = typeof value === 'object' ? value.amount : value;
        return isBoldRow ? (
          <span style={{ fontWeight: 'bold' }}>{(numValue || 0).toLocaleString()}원</span>
        ) : (
          <span>{(numValue || 0).toLocaleString()}원</span>
        );
      },
      align: 'right',
    },
    {
      title: '합계',
      dataIndex: 'total',
      key: 'total',
      width: 150,
      render: (value: number | { amount: number; percentage: string }, record: any) => {
        const isBoldRow =
          record.key === 'totalBillingAmount' ||
          record.key === 'profit' ||
          record.key === 'netProfit' ||
          record.key === 'netTransactionAmount';

        // 총 환불 금액 행인 경우 퍼센트와 함께 표시
        if (record.key === 'totalRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ fontWeight: isBoldRow ? 'bold' : 'normal' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#666' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대 환불 예상 금액 행인 경우 옅은 회색으로 표시
        if (record.key === 'totalMaxCalculatedRefundAmount' && typeof value === 'object') {
          return (
            <div>
              <div style={{ color: '#999' }}>{value.amount.toLocaleString()}원</div>
              <div style={{ fontSize: '12px', color: '#999' }}>({value.percentage}%)</div>
            </div>
          );
        }

        // 최대환불 예상 반영 항목들인 경우 옅은 회색으로 표시
        if (
          [
            'netTransactionAmountMaxRefundEstimated',
            'profitMaxRefundEstimated',
            'profitMaxRefundAndSettlementEstimated',
            'pgFeeMaxRefundEstimated',
            'netProfitMaxRefundEstimated',
            'netProfitMaxRefundAndSettlementEstimated',
          ].includes(record.key)
        ) {
          const numValue = typeof value === 'object' ? value.amount : value;
          return <span style={{ color: '#999' }}>{numValue.toLocaleString()}원</span>;
        }

        // 일반 숫자 값인 경우
        const numValue = typeof value === 'object' ? value.amount : value;
        return isBoldRow ? (
          <span style={{ fontWeight: 'bold' }}>{numValue.toLocaleString()}원</span>
        ) : (
          <span>{numValue.toLocaleString()}원</span>
        );
      },
      align: 'right',
    },
  ];

  // 데이터를 행 단위로 변환하는 함수
  const transformDataToRows = (data: NetSalesReportData[]) => {
    const channelData: { [key: string]: NetSalesReportData } = {};
    data.forEach((item) => {
      channelData[item.channel] = item;
    });

    const rows = [
      {
        key: 'totalPaymentAmount',
        item: '총 결제 금액',
        app: Math.round(channelData['app']?.totalPaymentAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalPaymentAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalPaymentAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalPaymentAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalPaymentAmount, 0)),
      },
      {
        key: 'totalPointAmount',
        item: '총 포인트 사용 금액',
        app: Math.round(channelData['app']?.totalPointAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalPointAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalPointAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalPointAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalPointAmount, 0)),
      },
      {
        key: 'totalCouponDiscountAmount',
        item: '총 쿠폰 사용 금액',
        app: Math.round(channelData['app']?.totalCouponDiscountAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalCouponDiscountAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalCouponDiscountAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalCouponDiscountAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalCouponDiscountAmount, 0)),
      },
      {
        key: 'totalMembershipDiscountAmount',
        item: '총 멤버십 할인 금액',
        app: Math.round(channelData['app']?.totalMembershipDiscountAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalMembershipDiscountAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalMembershipDiscountAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalMembershipDiscountAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalMembershipDiscountAmount, 0)),
      },
      {
        key: 'totalBillingAmount',
        item: <div style={{ fontWeight: 'bold' }}>실결제 금액</div>,
        app: Math.round(channelData['app']?.totalBillingAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalBillingAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalBillingAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalBillingAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalBillingAmount, 0)),
      },
      {
        key: 'totalRefundAmount',
        item: '총 환불 금액',
        app: {
          amount: Math.round(channelData['app']?.totalRefundAmount || 0),
          percentage: channelData['app']?.totalPaymentAmount
            ? (((channelData['app']?.totalRefundAmount || 0) / channelData['app']?.totalPaymentAmount) * 100).toFixed(1)
            : '0.0',
        },
        'naver-store': {
          amount: Math.round(channelData['naver-store']?.totalRefundAmount || 0),
          percentage: channelData['naver-store']?.totalPaymentAmount
            ? (
                ((channelData['naver-store']?.totalRefundAmount || 0) /
                  channelData['naver-store']?.totalPaymentAmount) *
                100
              ).toFixed(1)
            : '0.0',
        },
        'kakao-reservation': {
          amount: Math.round(channelData['kakao-reservation']?.totalRefundAmount || 0),
          percentage: channelData['kakao-reservation']?.totalPaymentAmount
            ? (
                ((channelData['kakao-reservation']?.totalRefundAmount || 0) /
                  channelData['kakao-reservation']?.totalPaymentAmount) *
                100
              ).toFixed(1)
            : '0.0',
        },
        ezwel: {
          amount: Math.round(channelData['ezwel']?.totalRefundAmount || 0),
          percentage: channelData['ezwel']?.totalPaymentAmount
            ? (
                ((channelData['ezwel']?.totalRefundAmount || 0) / channelData['ezwel']?.totalPaymentAmount) *
                100
              ).toFixed(1)
            : '0.0',
        },
        total: {
          amount: Math.round(data.reduce((sum, item) => sum + item.totalRefundAmount, 0)),
          percentage:
            data.reduce((sum, item) => sum + item.totalPaymentAmount, 0) > 0
              ? (
                  (data.reduce((sum, item) => sum + item.totalRefundAmount, 0) /
                    data.reduce((sum, item) => sum + item.totalPaymentAmount, 0)) *
                  100
                ).toFixed(1)
              : '0.0',
        },
      },
      {
        key: 'totalMaxCalculatedRefundAmount',
        item: <div style={{ color: '#999' }}>최대환불예상금액</div>,
        app: {
          amount: Math.round(channelData['app']?.estimatedTotalRefundBillingAmount || 0),
          percentage: '34.5',
        },
        'naver-store': {
          amount: Math.round(channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0),
          percentage: '32.9',
        },
        'kakao-reservation': {
          amount: Math.round(channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0),
          percentage: '48.3',
        },
        ezwel: {
          amount: Math.round(channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0),
          percentage: '34.5',
        },
        total: {
          amount: Math.round(
            (channelData['app']?.estimatedTotalRefundBillingAmount || 0) +
              (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0) +
              (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0) +
              (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0),
          ),
          percentage: '0.0', // 총합은 개별 퍼센트를 표시하지 않음
        },
      },
      {
        key: 'netTransactionAmountMaxRefundEstimated',
        item: <div style={{ color: '#999' }}>순거래액(최대환불예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) - (channelData['app']?.estimatedTotalRefundBillingAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedTotalRefundBillingAmount, 0),
        ),
      },
      {
        key: 'netTransactionAmount',
        item: <div style={{ fontWeight: 'bold' }}>순거래액</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) - (channelData['app']?.totalRefundBillingAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.totalRefundBillingAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalRefundBillingAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) - (channelData['ezwel']?.totalRefundBillingAmount || 0),
        ),
        total: Math.round(
          data.reduce(
            (sum, item) =>
              sum +
              (item.totalPaymentAmount -
                item.totalPointAmount -
                item.totalCouponDiscountAmount -
                item.totalMembershipDiscountAmount -
                item.totalRefundAmount),
            0,
          ),
        ),
      },
      {
        key: 'totalEstimatedSettlementAmount',
        item: '총 정산예상금액',
        app: Math.round(channelData['app']?.totalEstimatedSettlementAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalEstimatedSettlementAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalEstimatedSettlementAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalEstimatedSettlementAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalEstimatedSettlementAmount, 0)),
      },
      {
        key: 'totalTransferAmount',
        item: '총 정산금액',
        app: Math.round(channelData['app']?.totalTransferAmount || 0),
        'naver-store': Math.round(channelData['naver-store']?.totalTransferAmount || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.totalTransferAmount || 0),
        ezwel: Math.round(channelData['ezwel']?.totalTransferAmount || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.totalTransferAmount, 0)),
      },
      {
        key: 'profitMaxRefundEstimated',
        item: <div style={{ color: '#999' }}>매출이익(최대환불예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['app']?.totalTransferAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalTransferAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalTransferAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalTransferAmount || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedTotalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalTransferAmount, 0),
        ),
      },
      {
        key: 'profitMaxRefundAndSettlementEstimated',
        item: <div style={{ color: '#999' }}>매출이익(최대환불/정산예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['app']?.totalEstimatedSettlementAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalEstimatedSettlementAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalEstimatedSettlementAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalEstimatedSettlementAmount || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedTotalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalEstimatedSettlementAmount, 0),
        ),
      },
      {
        key: 'profitNotSettled',
        item: <div>매출이익(정산예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.totalRefundBillingAmount || 0) -
            (channelData['app']?.totalEstimatedSettlementAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.totalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalEstimatedSettlementAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalEstimatedSettlementAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.totalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalEstimatedSettlementAmount || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalEstimatedSettlementAmount, 0),
        ),
      },
      {
        key: 'profit',
        item: <div style={{ fontWeight: 'bold' }}>매출이익(정산반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.totalRefundBillingAmount || 0) -
            (channelData['app']?.totalTransferAmount || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.totalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalTransferAmount || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalTransferAmount || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.totalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalTransferAmount || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalTransferAmount, 0),
        ),
      },
      {
        key: 'pgFee',
        item: 'PG 및 채널 수수료',
        app: Math.round(channelData['app']?.pgFee || 0),
        'naver-store': Math.round(channelData['naver-store']?.pgFee || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.pgFee || 0),
        ezwel: Math.round(channelData['ezwel']?.pgFee || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.pgFee, 0)),
      },
      {
        key: 'pgFeeMaxRefundEstimated',
        item: <div style={{ color: '#999' }}>PG 및 채널 수수료(최대환불예상반영)</div>,
        app: Math.round(channelData['app']?.estimatedPgFee || 0),
        'naver-store': Math.round(channelData['naver-store']?.estimatedPgFee || 0),
        'kakao-reservation': Math.round(channelData['kakao-reservation']?.estimatedPgFee || 0),
        ezwel: Math.round(channelData['ezwel']?.estimatedPgFee || 0),
        total: Math.round(data.reduce((sum, item) => sum + item.estimatedPgFee, 0)),
      },
      {
        key: 'netProfitMaxRefundEstimated',
        item: <div style={{ color: '#999' }}>순이익(최대환불예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['app']?.totalTransferAmount || 0) -
            (channelData['app']?.estimatedPgFee || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalTransferAmount || 0) -
            (channelData['naver-store']?.estimatedPgFee || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalTransferAmount || 0) -
            (channelData['kakao-reservation']?.estimatedPgFee || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalTransferAmount || 0) -
            (channelData['ezwel']?.estimatedPgFee || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedTotalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalTransferAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedPgFee, 0),
        ),
      },
      {
        key: 'netProfitMaxRefundAndSettlementEstimated',
        item: <div style={{ color: '#999' }}>순이익(최대환불/정산예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['app']?.totalEstimatedSettlementAmount || 0) -
            (channelData['app']?.estimatedPgFee || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalEstimatedSettlementAmount || 0) -
            (channelData['naver-store']?.estimatedPgFee || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalEstimatedSettlementAmount || 0) -
            (channelData['kakao-reservation']?.estimatedPgFee || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.estimatedTotalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalEstimatedSettlementAmount || 0) -
            (channelData['ezwel']?.estimatedPgFee || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedTotalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalEstimatedSettlementAmount, 0) -
            data.reduce((sum, item) => sum + item.estimatedPgFee, 0),
        ),
      },
      {
        key: 'netProfitEstimated',
        item: <div>순이익(정산예상반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.totalRefundBillingAmount || 0) -
            (channelData['app']?.totalEstimatedSettlementAmount || 0) -
            (channelData['app']?.pgFee || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.totalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalEstimatedSettlementAmount || 0) -
            (channelData['naver-store']?.pgFee || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalEstimatedSettlementAmount || 0) -
            (channelData['kakao-reservation']?.pgFee || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.totalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalEstimatedSettlementAmount || 0) -
            (channelData['ezwel']?.pgFee || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalEstimatedSettlementAmount, 0) -
            data.reduce((sum, item) => sum + item.pgFee, 0),
        ),
      },
      {
        key: 'netProfit',
        item: <div style={{ fontWeight: 'bold' }}>순이익(정산반영)</div>,
        app: Math.round(
          (channelData['app']?.totalBillingAmount || 0) -
            (channelData['app']?.totalRefundBillingAmount || 0) -
            (channelData['app']?.totalTransferAmount || 0) -
            (channelData['app']?.pgFee || 0),
        ),
        'naver-store': Math.round(
          (channelData['naver-store']?.totalBillingAmount || 0) -
            (channelData['naver-store']?.totalRefundBillingAmount || 0) -
            (channelData['naver-store']?.totalTransferAmount || 0) -
            (channelData['naver-store']?.pgFee || 0),
        ),
        'kakao-reservation': Math.round(
          (channelData['kakao-reservation']?.totalBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalRefundBillingAmount || 0) -
            (channelData['kakao-reservation']?.totalTransferAmount || 0) -
            (channelData['kakao-reservation']?.pgFee || 0),
        ),
        ezwel: Math.round(
          (channelData['ezwel']?.totalBillingAmount || 0) -
            (channelData['ezwel']?.totalRefundBillingAmount || 0) -
            (channelData['ezwel']?.totalTransferAmount || 0) -
            (channelData['ezwel']?.pgFee || 0),
        ),
        total: Math.round(
          data.reduce((sum, item) => sum + item.totalBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalRefundBillingAmount, 0) -
            data.reduce((sum, item) => sum + item.totalTransferAmount, 0) -
            data.reduce((sum, item) => sum + item.pgFee, 0),
        ),
      },
    ];

    return rows;
  };

  // 채널별 순이익 도넛차트 데이터 생성
  const doughnutChartData = useMemo(() => {
    if (data.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const channelLabels: { [key: string]: string } = {
      app: '앱',
      'naver-store': '네이버스토어',
      'kakao-reservation': '카카오 예약',
      ezwel: '이지웰',
    };

    const colors = {
      app: 'rgb(24, 144, 255)',
      'naver-store': 'rgb(3, 199, 90)',
      'kakao-reservation': '#FEE500',
      ezwel: '#0D6EFD',
    };

    // 채널별 순이익 계산
    const channelNetProfits = data.map((item) => {
      const netProfit = Math.round(
        item.totalBillingAmount - item.totalRefundBillingAmount - item.totalEstimatedSettlementAmount - item.pgFee,
      );
      return {
        channel: item.channel,
        netProfit: netProfit,
        label: channelLabels[item.channel] || item.channel,
        color: colors[item.channel as keyof typeof colors] || 'rgb(128, 128, 128)',
      };
    });

    // 양수인 순이익만 필터링 (음수는 제외)
    const positiveProfits = channelNetProfits.filter((item) => item.netProfit > 0);

    if (positiveProfits.length === 0) {
      return {
        labels: ['데이터 없음'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['rgb(200, 200, 200)'],
            borderColor: ['rgb(200, 200, 200)'],
            borderWidth: 1,
          },
        ],
      };
    }

    return {
      labels: positiveProfits.map((item) => item.label),
      datasets: [
        {
          data: positiveProfits.map((item) => item.netProfit),
          backgroundColor: positiveProfits.map((item) => item.color),
          borderColor: positiveProfits.map((item) => item.color),
          borderWidth: 2,
        },
      ],
    };
  }, [data]);

  // 일별 순이익 차트 데이터 생성
  const dailyChartData = useMemo(() => {
    if (dailyData.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // 날짜별로 정렬 (안전한 정렬) - 더 유연한 필터링
    const sortedData = [...dailyData]
      .filter((item) => {
        const hasDate = item.date && typeof item.date === 'string' && item.date.trim() !== '';
        return hasDate; // 유효한 date가 있는 항목만 필터링
      })
      .sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date.localeCompare(b.date);
      });

    // sortedData가 비어있는 경우 처리
    if (sortedData.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const dates = Array.from(new Set(sortedData.map((item) => item.date))).sort();
    const channels = Array.from(new Set(sortedData.map((item) => item.channel)));

    const channelLabels: { [key: string]: string } = {
      app: '앱',
      'naver-store': '네이버스토어',
      'kakao-reservation': '카카오 예약',
      ezwel: '이지웰',
    };

    const colors = {
      app: 'rgb(24, 144, 255)',
      'naver-store': 'rgb(3, 199, 90)',
      'kakao-reservation': '#FEE500',
      ezwel: '#0D6EFD',
    };

    const datasets = channels.map((channel) => {
      const channelData = dates.map((date) => {
        const item = sortedData.find((d) => d.date === date && d.channel === channel);
        return item ? item.netProfit : 0;
      });

      return {
        label: channelLabels[channel] || channel,
        data: channelData,
        borderColor: colors[channel as keyof typeof colors] || 'rgb(128, 128, 128)',
        backgroundColor: colors[channel as keyof typeof colors] || 'rgb(128, 128, 128)',
        fill: false,
        tension: 0.1,
      };
    });

    const result = {
      labels: dates,
      datasets,
    };

    return result;
  }, [dailyData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}원`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: '날짜',
        },
      },
      y: {
        beginAtZero: true,
        display: true,
        title: {
          display: true,
          text: '순이익 (원)',
        },
        ticks: {
          callback: function (value: any) {
            return value.toLocaleString() + '원';
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toLocaleString()}원 (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>이익관리</h2>

      {/* 검색 조건 */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <span>조회 기간:</span>
          </Col>
          <Col>
            <DatePicker
              value={dayjs(searchDate.startDate)}
              onChange={(date) => {
                if (date) {
                  setSearchDate((prev) => ({ ...prev, startDate: date.toDate() }));
                }
              }}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col>
            <span>~</span>
          </Col>
          <Col>
            <DatePicker
              value={dayjs(searchDate.endDate)}
              onChange={(date) => {
                if (date) {
                  setSearchDate((prev) => ({ ...prev, endDate: date.toDate() }));
                }
              }}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col>
            <Button type="primary" onClick={handleSearch} loading={loading}>
              조회
            </Button>
          </Col>
          <Col>
            <Button onClick={handleExport} disabled={data.length === 0}>
              엑셀 다운로드
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 차트 영역 (추후 구현 예정) */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card title="채널별 순이익 비율" style={{ height: '300px' }}>
            {doughnutChartData.labels.length > 0 ? (
              <div style={{ height: '200px', position: 'relative' }}>
                <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#999',
                  fontSize: '14px',
                }}>
                데이터를 조회해주세요
              </div>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="일별 순이익 추이" style={{ height: '300px' }}>
            {dailyChartData.labels.length > 0 && dailyChartData.datasets.length > 0 ? (
              <div style={{ height: '200px', position: 'relative' }}>
                <Line data={dailyChartData} options={chartOptions} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#999',
                  fontSize: '14px',
                }}>
                {loading ? '데이터를 불러오는 중...' : '데이터를 조회해주세요'}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 데이터 테이블 */}
      <Card title="채널별 매출 상세">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={transformDataToRows(data)}
            rowKey="key"
            pagination={false}
            scroll={{ x: 800 }}
            rowClassName={(record) => {
              if (record.key === 'netTransactionAmount' || record.key === 'profit') {
                return 'net-transaction-row';
              }
              return '';
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default NetSalesReportPage;
